import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { PrimaryButton, IconSpinner, IconDelete, NamedLink } from '../../components';

import css from './AddToCartButton.module.css';

/**
 * AddToCartButton replaces "Buy Now" on product listing pages.
 * Shows "Add to Cart" if item is not in the cart.
 * Once in the cart: "In your basket", a link to the basket, and Remove.
 * Adds one copy only; quantities are changed on the basket page.
 *
 * @param {Object} props
 * @param {number} props.currentCount - How many of this listing are in the cart (0 if not added)
 * @param {number} props.currentStock - Available stock for this listing
 * @param {boolean} props.isOwnListing - Whether the listing belongs to the current user
 * @param {Function} props.onAddToCart - Called with increment (+1 or -1)
 * @param {boolean} props.inProgress - Whether a cart update is in progress
 * @param {string} [props.cartError] - Error message from cart update
 * @param {string} [props.sellerId] - The listing author's id, so "View basket" opens at their books
 * @param {string} [props.className] - Optional extra CSS class
 */
const AddToCartButton = props => {
  const {
    currentCount = 0,
    currentStock = 0,
    isOwnListing,
    onAddToCart,
    inProgress = false,
    cartError,
    sellerId,
    className,
  } = props;

  const isOutOfStock = currentStock === 0;
  const isInCart = currentCount > 0;

  if (isOwnListing) {
    return null;
  }

  const classes = classNames(css.root, className);

  if (isOutOfStock) {
    return (
      <div className={classes}>
        <PrimaryButton disabled className={css.addButton}>
          <FormattedMessage id="AddToCartButton.outOfStock" />
        </PrimaryButton>
      </div>
    );
  }

  if (!isInCart) {
    return (
      <div className={classes}>
        <PrimaryButton
          className={css.addButton}
          onClick={() => onAddToCart(1)}
          disabled={inProgress}
        >
          {inProgress ? <IconSpinner /> : <FormattedMessage id="AddToCartButton.addToCart" />}
        </PrimaryButton>
        {cartError ? <p className={css.error}>{cartError}</p> : null}
      </div>
    );
  }

  // The listing page only ever adds one copy; buyers rarely want more of the
  // same book, so choosing a quantity is left to the basket page. Here the
  // book is simply in or out: a card whose header says so (with Remove beside
  // it, so it plainly means "out of the basket"), and the next step, View
  // basket, as the main button. The header is what tells it apart from Add.
  return (
    <div className={classes}>
      <div className={css.inCartCard}>
        <div className={css.inCartHeader}>
          <span className={css.inCartLabel}>
            <svg
              className={css.tick}
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" />
            </svg>
            <FormattedMessage id="AddToCartButton.inCart" />
          </span>
          <button
            type="button"
            className={css.removeButton}
            onClick={() => onAddToCart(-currentCount)}
            disabled={inProgress}
          >
            <IconDelete className={css.removeIcon} />
            <FormattedMessage id="AddToCartButton.remove" />
          </button>
        </div>
        <NamedLink
          name="CartPage"
          to={sellerId ? { search: `?seller=${sellerId}` } : {}}
          className={css.viewBasketButton}
        >
          <FormattedMessage id="AddToCartButton.goToBasket" />
        </NamedLink>
      </div>
      {cartError ? <p className={css.error}>{cartError}</p> : null}
    </div>
  );
};

export default AddToCartButton;
